import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import useCruiseSearchMpvRankingFilters from 'hooks/Cruise/useCruiseSearchMpvRankingFilters'

interface Props {
  onClear: () => void
  onApply: () => void
  initialFilters?: App.OfferListFilters
}

function CruiseFilterFooter({
  onClear,
  onApply,
  initialFilters,
}: Props) {
  const intl = useIntl()
  const globalFilters = useContext(GlobalSearchStateContext)
  const {
    cruiseLines,
    searchItems,
    secondarySearchItems,
    durationMax,
    durationMin,
    durationRange,
    flexibleMonths,
    checkinDate,
    checkoutDate,
  } = globalFilters

  const countFilters = useCruiseSearchMpvRankingFilters({
    cruiseLines,
    destinationIds: searchItems.map(({ value }) => value),
    departureIds: secondarySearchItems.map(({ value }) => value),
    durationMin,
    durationMax,
    durationRange,
    departureMonths: flexibleMonths?.split(',') || EmptyArray,
    ...(!!checkinDate && !!checkoutDate && {
      departureStartDate: checkinDate.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate.format(ISO_DATE_FORMAT),
    }),
    ...(initialFilters || {}),
  })
  const [searchCount, fetchingSearchCount] = useCruiseSearchCount(countFilters)

  return <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
    <BodyText data-testid="cruise-filter-footer-available-results" variant="medium">
      {!fetchingSearchCount && intl.formatMessage({
        id: '-gEOpq_QWkWdLgsbe80E',
        defaultMessage: '{count, plural, one {# cruise available} other {# cruises available}}',
      }, { count: searchCount })}
      {fetchingSearchCount && <LoadingAnimation size={8} />}
    </BodyText>

    <Group direction="horizontal" horizontalAlign="end" gap={16}>
      <TextButton
        kind="tertiary"
        onClick={onClear}
        data-testid="cruise-filter-footer-clear-all"
      >
        {intl.formatMessage({
          id: 'CLX1JYm50NiyJHejnrA2',
          defaultMessage: 'Reset all',
        })}
      </TextButton>
      <TextButton
        kind="primary"
        onClick={onApply}
        data-testid="cruise-filter-footer-apply"
      >
        {intl.formatMessage({
          id: 'zZNuM1A0SgjmEKYQ4Y1s',
          defaultMessage: 'Apply',
        })}
      </TextButton>
    </Group>
  </Group>
}

export default CruiseFilterFooter
